// Shared visual identity + derive helpers for the three testing flows
// (Dashboard / Extension / Exe). Used by the admin Overview + Analytics tabs so
// every flow reads with the same color, icon, and math everywhere.
import { LayoutDashboard, Puzzle, Bot } from "lucide-react";

export const FLOWS = [
  {
    key: "dashboard", label: "Dashboard", icon: LayoutDashboard,
    blurb: "Cloud runs from the web app",
    // Cloud/headless testing is PAUSED — the tester nav disables the tab and the
    // Runs & Bugs flow map already marks this source inert. Surfaces answering
    // "what is happening now" must skip it, or they render a permanently 0-run,
    // 0%-pass card beside two live ones. Analysis surfaces keep listing it so
    // historical cloud runs stay reachable.
    disabled: true,
    stroke: "#6366f1",
    chip: "bg-brand-500/10 text-brand-600 dark:text-brand-300",
    bar: "bg-brand-500", ring: "ring-brand-500/30", text: "text-brand-600 dark:text-brand-300",
    grad: "from-brand-500/15 to-brand-500/0",
  },
  {
    key: "extension", label: "Extension", icon: Puzzle,
    blurb: "Headed runs in a real Chrome tab",
    stroke: "#0ea5e9",
    chip: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
    bar: "bg-sky-500", ring: "ring-sky-500/30", text: "text-sky-600 dark:text-sky-300",
    grad: "from-sky-500/15 to-sky-500/0",
  },
  {
    key: "exe", label: "Exe Agent", icon: Bot,
    blurb: "Runs on a tester's machine",
    stroke: "#8b5cf6",
    chip: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
    bar: "bg-violet-500", ring: "ring-violet-500/30", text: "text-violet-600 dark:text-violet-300",
    grad: "from-violet-500/15 to-violet-500/0",
  },
];

export const FLOW_BY_KEY = Object.fromEntries(FLOWS.map((f) => [f.key, f]));

// Flows still accepting new runs. Use this for "right now" views; use FLOWS when
// a retired flow's historical data must stay visible.
export const ACTIVE_FLOWS = FLOWS.filter((f) => !f.disabled);

// What one run cost, whichever runner produced it. Mirrors the key order in
// backend/core/costs.py: the exe agent writes `cost_usd` while the extension and
// cloud runners write `estimated_cost_usd`. Reading only the latter valued EVERY
// exe run at $0 — the exe flow card, its Analytics cost bar and the Cost column
// on the exe runs table all read $0.00 however much had been spent, and nothing
// distinguished that from a genuinely free replay.
const COST_KEYS = ["estimated_cost_usd", "cost_usd", "total_cost_usd", "cost"];

export function runCost(tokenUsage) {
  if (!tokenUsage || typeof tokenUsage !== "object") return 0;
  for (const key of COST_KEYS) {
    const value = tokenUsage[key];
    if (value == null) continue;
    const n = Number(value);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

// Normalize any flow's run (list item) into one common shape.
export function normalizeRun(r, flow) {
  const cost = runCost(r.token_usage);
  return {
    flow,
    id: r.id,
    status: String(r.status || "").toLowerCase(),
    // Carried through so isBug() can tell a real bug report from a mere failure.
    // Dropping it here silently forced every consumer of a normalized run down
    // isBug's legacy fallback (has_bug === undefined -> isFail), so the tester
    // dashboard counted failures as bugs while the company page, which reads the
    // raw rows, counted them correctly. Same data, two different numbers.
    has_bug: r.has_bug,
    cost,
    duration: r.duration_seconds || 0,
    browser: r.browser || null,
    tester: r.tester || null,
    instruction: r.instruction || "",
    url: r.url || "",
    createdAt: r.created_at,
  };
}

export const isPass = (s) => String(s).toLowerCase() === "passed";
export const isFail = (s) => ["failed", "error"].includes(String(s).toLowerCase());

// A BUG is a run that produced a bug report — not merely one that failed.
//
// `has_bug` comes from the run list. A payload without it (an older backend, or
// the cloud runner, which has no such field) degrades to the failed status, which
// is the convention those screens used before the flag existed — so nothing
// silently reads zero after an upgrade.
//
// Shared so Analytics and Runs & Bugs cannot disagree about the same number.
export const isBug = (r) =>
  (r && r.has_bug === undefined ? isFail(r.status) : !!(r && r.has_bug));

// Aggregate a list of normalized runs into headline stats.
export function flowStats(runs) {
  const total = runs.length;
  const passed = runs.filter((r) => isPass(r.status)).length;
  const failed = runs.filter((r) => isFail(r.status)).length;
  const cost = runs.reduce((s, r) => s + (r.cost || 0), 0);
  const withDur = runs.filter((r) => r.duration > 0);
  const avgDur = withDur.length ? withDur.reduce((s, r) => s + r.duration, 0) / withDur.length : 0;
  return {
    total, passed, failed, cost, avgDur,
    passRate: total ? Math.round((passed / total) * 100) : 0,
  };
}

const DOW = ["S", "M", "T", "W", "T", "F", "S"];
// Three-letter weekdays for axes with room for them. DOW above stays
// single-letter for the dense sparkline-sized charts that already use it.
const DOW_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Bucket normalized runs into the last `days` calendar days → { labels, titles, data }.
//
// `labels` is what the x-axis prints and `titles` is the full date every bar puts
// in its tooltip. They diverge past a week: 30 day-letters is an unreadable smear,
// so a wide window prints ~6 dated ticks and leaves the rest blank — but every bar
// still names its own day on hover.
export function perDaySeries(runs, days = 7) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    buckets.push({
      key: d.getTime(),
      dow: DOW[d.getDay()],
      title: `${d.getDate()} ${MON[d.getMonth()]}`,
      n: 0,
    });
  }
  const index = new Map(buckets.map((b) => [b.key, b]));
  runs.forEach((r) => {
    const d = new Date(r.createdAt);
    if (Number.isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0);
    const b = index.get(d.getTime());
    if (b) b.n += 1;
  });
  // Counted back from the END so the most recent day is always labelled — a
  // trailing unlabelled tick reads as though the chart stops short of today.
  const step = days <= 7 ? 1 : Math.ceil(days / 6);
  const labels = buckets.map((b, i) =>
    days <= 7 ? b.dow : ((days - 1 - i) % step === 0 ? b.title : "")
  );
  return { labels, titles: buckets.map((b) => b.title), data: buckets.map((b) => b.n) };
}

// The x-axis for a series the SERVER already bucketed by day.
//
// `perDaySeries` above buckets a client-side run list and labels it in one go.
// A page whose buckets arrive from the API (platform Analytics) needs the same
// labels without re-bucketing anything, and must not invent a different rule —
// two charts in one product that date their bars differently is how a reader
// stops trusting either.
//
// So: the identical thinning. A week or less names every bar by weekday;
// anything longer labels every ceil(days/6)-th bar, counted BACK FROM THE END so
// the most recent day is always named (a trailing unlabelled tick reads as
// though the chart stops short of today). `titles` is always a full date, for
// tooltips, because the visible label goes sparse.
//
// Dates arrive as bare "YYYY-MM-DD" bucketed in UTC. `new Date("2026-06-15")`
// parses as UTC midnight while getDay()/getDate() read the LOCAL calendar, so
// every label slid back a day for viewers west of UTC. Built from the parts
// instead — a day that was never an instant cannot be shifted by a timezone.
export function dateAxis(dates) {
  const list = Array.isArray(dates) ? dates : [];
  const parts = list.map((iso) => {
    const [y, m, d] = String(iso || "").split("-").map(Number);
    if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
    return new Date(y, m - 1, d);
  });
  const titles = parts.map((d) => (d ? `${d.getDate()} ${MON[d.getMonth()]}` : ""));
  const days = list.length;
  const step = days <= 7 ? 1 : Math.ceil(days / 6);
  const labels = parts.map((d, i) => {
    if (!d) return "";
    if (days <= 7) return DOW_FULL[d.getDay()];
    return (days - 1 - i) % step === 0 ? titles[i] : "";
  });
  return { labels, titles };
}

// Windows the activity charts may fall back through, narrowest first.
export const ACTIVITY_WINDOWS = [7, 30, 90];

// Widest-useful window: the narrowest one that actually contains a run.
//
// A fixed 7 days made a live account look broken. A team whose last run was 8
// days ago got an empty chart under a card reading "333 runs" — two true numbers
// that contradict each other on screen, which reads as a failed chart rather than
// as a quiet week. Falling back keeps the default tight when work is recent and
// still shows the data when it isn't.
//
// Tested with perDaySeries rather than a rolling millisecond cutoff so the answer
// agrees exactly with the buckets the chart will draw.
export function pickActivityWindow(runs, windows = ACTIVITY_WINDOWS) {
  for (const days of windows) {
    if (perDaySeries(runs, days).data.some((n) => n > 0)) return days;
  }
  return windows[0];
}

// Keep only runs inside the last `days` days (client-side range filter).
export function withinDays(runs, days) {
  if (!days) return runs;
  const cutoff = Date.now() - days * 86400000;
  return runs.filter((r) => {
    const t = new Date(r.createdAt).getTime();
    return Number.isNaN(t) ? true : t >= cutoff;
  });
}
